import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import {
  AddressSuggestion,
  LocationDisplay,
  StreetDisplay,
  SwissPostAutocompleteResponse,
  SwissPostSearchParams,
  SwissPostVerificationResponse,
} from '@app/models/address.model';
import { ApiService } from '@app/services/api.service';
import { catchError, lastValueFrom, map, Observable, of } from 'rxjs';

/** Result type for location search */
export interface LocationSearchResult {
  displayStrings: string[];
  lookupMap: Map<string, LocationDisplay>;
}

/** Result type for street search */
export interface StreetSearchResult {
  displayStrings: string[];
  lookupMap: Map<string, StreetDisplay>;
}

/** PSTAT validation result */
export interface PstatResult {
  isValid: boolean;
  isCorrected: boolean;
  isAmbiguous: boolean;
}

/**
 * Service for interacting with Swiss Post Address API
 * Provides autocomplete and verification functionality for Swiss addresses
 */
@Service()
export class SwissPostAddressService {
  #api = inject(ApiService);
  readonly #autocompleteEndpoint = `${this.#api.apiUrl}/swisspost/autocomplete`;
  readonly #verifyEndpoint = `${this.#api.apiUrl}/swisspost/verify`;
  readonly #http = inject(HttpClient);

  /**
   * Search for locations (PLZ + town) using Swiss Post autocomplete4 API
   * @param searchTerm - The search term (postal code, town name, or combination like "3000 Ber")
   */
  searchLocations(searchTerm: string): Observable<LocationSearchResult> {
    const params = new URLSearchParams();
    const trimmedTerm = searchTerm.trim();

    // Check if term is purely numeric (postal code only)
    if (/^\d+$/.test(trimmedTerm)) {
      // Pure digits: search by zip code
      params.set('zipCode', trimmedTerm);
    } else if (/^(\d+)\s+(.+)$/.test(trimmedTerm)) {
      // Mixed format like "3182 U" or "3182 Ueberstorf": extract zip and town
      const match = trimmedTerm.match(/^(\d+)\s+(.+)$/);
      if (match) {
        params.set('zipCode', match[1]);
        params.set('townName', match[2]);
      }
    } else {
      // Check for combined format like "3000 Ber" or "3000 Bern"
      const combinedMatch = trimmedTerm.match(/^(\d+)\s+(.+)$/);
      if (combinedMatch) {
        // Combined search: set both zipCode and townName
        params.set('zipCode', combinedMatch[1]);
        params.set('townName', combinedMatch[2]);
      } else {
        // Pure text search (town name only)
        params.set('townName', trimmedTerm);
      }
    }

    return this.#http
      .get<SwissPostAutocompleteResponse>(
        `${this.#autocompleteEndpoint}?${params.toString()}`
      )
      .pipe(
        map((response) => this.#mapLocationsResponse(response)),
        catchError((error) => {
          console.error('Error fetching locations:', error);
          return of({ displayStrings: [], lookupMap: new Map() });
        })
      );
  }

  /**
   * Search for streets using Swiss Post autocomplete4 API
   * @param searchTerm - The street name to search for
   * @param filterParams - Additional parameters for filtering (onrp, zipCode, townName)
   */
  searchStreets(
    searchTerm: string,
    filterParams: SwissPostSearchParams
  ): Observable<StreetSearchResult> {
    const params = new URLSearchParams();
    params.set('streetName', searchTerm.trim());

    // Use ONRP for exact town filtering if available
    if (filterParams.onrp) {
      params.set('onrp', filterParams.onrp);
    } else {
      // Fallback to zip code and town name
      if (filterParams.zipCode) {
        params.set('zipCode', filterParams.zipCode);
      }
      if (filterParams.townName) {
        params.set('townName', filterParams.townName);
      }
    }

    return this.#http
      .get<SwissPostAutocompleteResponse>(
        `${this.#autocompleteEndpoint}?${params.toString()}`
      )
      .pipe(
        map((response) => this.#mapStreetsResponse(response)),
        catchError((error) => {
          console.error('Error fetching streets:', error);
          return of({ displayStrings: [], lookupMap: new Map() });
        })
      );
  }

  /**
   * Verify address using Swiss Post BuildingVerification4 API
   * @param address - The address to verify
   * @returns Promise of AddressSuggestion if valid, null otherwise
   */
  async verifyAddress(
    streetName: string,
    houseNo: string,
    zipCode: string,
    townName: string
  ): Promise<AddressSuggestion | null> {
    const params = new URLSearchParams();
    params.set('streetName', streetName);
    params.set('houseNo', houseNo);
    params.set('zipCode', zipCode);
    params.set('townName', townName);

    try {
      const response = await lastValueFrom(
        this.#http.get<SwissPostVerificationResponse>(
          `${this.#verifyEndpoint}?${params.toString()}`
        )
      );

      if (response?.QueryBuildingVerification4Result?.Status === 0) {
        const data = response.QueryBuildingVerification4Result.BuildingVerificationData;
        return {
          streetName: data.StreetName,
          houseNo: data.HouseNo,
          houseNoAddition: data.HouseNoAddition,
          zipCode: data.ZipCode,
          townName: data.TownName,
          pstat: data.PSTAT,
        };
      }
    } catch (error) {
      console.error('Address verification failed:', error);
    }

    return null;
  }

  /**
   * Search for alternative addresses when verification fails
   */
  async searchAlternativeAddresses(
    params: SwissPostSearchParams
  ): Promise<AddressSuggestion[]> {
    const urlParams = new URLSearchParams();

    if (params.streetName) urlParams.set('streetName', params.streetName);
    if (params.houseNo) urlParams.set('houseNo', params.houseNo);
    if (params.strid) urlParams.set('strid', params.strid);

    // Use ONRP for exact filtering, or fall back to zip/town
    if (params.onrp) {
      urlParams.set('onrp', params.onrp);
    } else {
      if (params.zipCode) urlParams.set('zipCode', params.zipCode);
      if (params.townName) urlParams.set('townName', params.townName);
    }

    try {
      const response = await lastValueFrom(
        this.#http.get<SwissPostAutocompleteResponse>(
          `${this.#autocompleteEndpoint}?${urlParams.toString()}`
        )
      );

      const results = response?.QueryAutoComplete4Result?.AutoCompleteResult || [];

      return results.slice(0, 3).map((item) => ({
        streetName: item.StreetName,
        houseNo: item.HouseNo,
        houseNoAddition: item.HouseNoAddition,
        zipCode: item.ZipCode,
        townName: item.TownName,
        pstat: '',
      }));
    } catch (error) {
      console.error('Alternative address search failed:', error);
      return [];
    }
  }

  /**
   * Parse PSTAT value to determine address validity
   * @param pstat - The PSTAT value from verification response
   */
  parsePstat(pstat: string): PstatResult {
    const pstatNum = parseInt(pstat, 10);
    return {
      isValid: pstatNum >= 1 && pstatNum <= 5,
      isCorrected: pstatNum > 1 && pstatNum <= 5,
      isAmbiguous: pstatNum >= 7 && pstatNum <= 8,
    };
  }

  /**
   * Map autocomplete response to location display format
   */
  #mapLocationsResponse(response: SwissPostAutocompleteResponse): LocationSearchResult {
    const results = response?.QueryAutoComplete4Result?.AutoCompleteResult || [];
    const lookupMap = new Map<string, LocationDisplay>();
    const seen = new Set<string>();
    const displayStrings: string[] = [];

    for (const item of results) {
      const key = `${item.ZipCode}-${item.TownName}`;
      if (seen.has(key)) continue;
      seen.add(key);

      const display = `${item.ZipCode} ${item.TownName}`;
      displayStrings.push(display);
      lookupMap.set(display, {
        display,
        zipCode: item.ZipCode,
        townName: item.TownName,
        onrp: item.ONRP,
        data: item,
      });
    }

    return { displayStrings, lookupMap };
  }

  /**
   * Map autocomplete response to street display format
   */
  #mapStreetsResponse(response: SwissPostAutocompleteResponse): StreetSearchResult {
    const results = response?.QueryAutoComplete4Result?.AutoCompleteResult || [];
    const lookupMap = new Map<string, StreetDisplay>();
    const seen = new Set<string>();
    const displayStrings: string[] = [];

    for (const item of results) {
      if (!item.StreetName) continue;
      const key = `${item.STRID}-${item.StreetName}`;
      if (seen.has(key)) continue;
      seen.add(key);

      const display = item.StreetName;
      displayStrings.push(display);
      lookupMap.set(display, {
        display,
        streetName: item.StreetName,
        strid: item.STRID,
        data: item,
      });
    }

    return { displayStrings, lookupMap };
  }
}
